import { forwardRef, useCallback, useEffect } from "react";
import { Box, Container, Typography, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import {
  editAdjustment,
  selectAdjustment,
  selectDeniedCredits,
  updateAdjustment,
  updateAdjustmentDenied
} from "../createInventoryAdjustment.slice";
import { convertStringToCurrencyWithDecimalNegative } from "src/utils/genericFunctions";
import { useLocation } from "react-router-dom";
import { selectUserconfiguration } from "src/pages/Login/user.slice";
import GoTableWithSelect from "src/components/GoTableWithSelect/GoTableWithSelect";
import { HeaderRule } from "src/components/GoTableWithSelect/interface";

const createInvTableData = (
  item: any,
  type: string,
  isTableRowDisabled: boolean
) => {
  return {
    invbeerWine: item?.beerWine,
    invgrocery: item?.grocery,
    invfrozen: item?.frozen,
    invdeli: item?.deli,
    invhbc: item?.hbc,
    invgm: item?.gm,
    invproduce: item?.produce,
    invmeat: item?.meat,
    invtotal: item.total,
    invvaluation: item?.valuation,
    isDisabled: type === "denied" ? isTableRowDisabled : false,
    type
  };
};

const getEditAdjustmentData = (editData: any, key: string) => {
  const value = editData?.departments[key]?.toString();
  return value ? convertStringToCurrencyWithDecimalNegative(false, value) : "0";
};

export const transformDataToDepartment = (data: any) => {
  return {
    departments: {
      grocery: data.grocery,
      frozen: data.frozen,
      deli: data.deli,
      beerOrWine: data.beerorwine,
      hbc: data.hbc,
      gm: data.gm,
      produce: data.produce,
      meat: data.meat
    }
  };
};

export const getAdjItems = (editData: any, adjItems: any) => {
  return {
    ...adjItems,
    beerWine: {
      id: "beerWine",
      value:
        adjItems?.beerWine?.value?.toString() ||
        getEditAdjustmentData(editData, "beerOrWine")
    },
    grocery: {
      id: "grocery",
      value:
        adjItems?.grocery?.value?.toString() ||
        getEditAdjustmentData(editData, "grocery"),
      testId: "grocery-input"
    },
    frozen: {
      id: "frozen",
      value:
        adjItems?.frozen?.value?.toString() ||
        getEditAdjustmentData(editData, "frozen")
    },
    deli: {
      id: "deli",
      value:
        adjItems?.deli?.value?.toString() ||
        getEditAdjustmentData(editData, "deli")
    },
    hbc: {
      id: "hbc",
      value:
        adjItems?.hbc?.value?.toString() ||
        getEditAdjustmentData(editData, "hbc")
    },
    gm: {
      id: "gm",
      value:
        adjItems?.gm?.value?.toString() || getEditAdjustmentData(editData, "gm")
    },
    produce: {
      id: "produce",
      value:
        adjItems?.produce?.value?.toString() ||
        getEditAdjustmentData(editData, "produce")
    },
    meat: {
      id: "meat",
      value:
        adjItems?.meat?.value?.toString() ||
        getEditAdjustmentData(editData, "meat")
    },
    total: {
      id: "total",
      value:
        adjItems?.total?.value?.toString() ||
        getEditAdjustmentData(editData, "total"),
      disabled: true
    },
    valuation: {
      disabled: true,
      id: "valuation",
      isSeparated: true,
      noAdornment: true,
      type: "text",
      value: adjItems.valuation.value || editData?.valuation,
      hideInput: true,
      align: "left"
    }
  };
};

const Adjustment = forwardRef((_, ref) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const adjItems = useAppSelector(selectAdjustment);
  const deniedItems = useAppSelector(selectDeniedCredits);
  const location: any = useLocation();
  const referenceId = location?.state?.referenceId;
  const userConfiguration = useAppSelector(selectUserconfiguration);
  const editData = useAppSelector(
    (stateValue: any) => stateValue?.receiveTypes?.data?.pendingCredits
  );

  const editDeniedCredits = useAppSelector(
    (stateValue: any) => stateValue?.receiveTypes?.data?.deniedCredits
  );

  useEffect(() => {
    if (!editData) return;
    const newAdjItems = getAdjItems(editData, adjItems);
    const newAdjItemsDenied = editDeniedCredits
      ? getAdjItems(editDeniedCredits, deniedItems)
      : null;
    dispatch(updateAdjustment(newAdjItems));
    if (newAdjItemsDenied) {
      dispatch(updateAdjustmentDenied(newAdjItemsDenied));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData]);

  const handleUpdate = (name: string, id: string | number, value: any) => {
    const payload = {
      name,
      id,
      value
    };
    dispatch(editAdjustment(payload));
  };

  const commonTableHeaderAttr = {
    handleChange: handleUpdate,
    headerStyleOverrides: {
      width: "8%",
      padding: "8px 16px"
    },
    dataCellStyleOverrides: { padding: "10px 16px" },
    isSeparated: true
  };

  const headerRules: HeaderRule[] = Object.values(adjItems).map(
    (item: any, index: number) => {
      return {
        id: "inv" + item?.id,
        label: t(`createInventoryAdjustment.table.${item.id}`),
        editMode: item?.type !== "text",
        align: `${index >= 1 ? "right" : "left"}`,
        ...commonTableHeaderAttr
      };
    }
  );

  const getInvTableData = useCallback(() => {
    const isDeniedRowDisabled =
      userConfiguration !== "Inventory Accounting User" && !referenceId;
    const adjTableItem = createInvTableData(adjItems, "pending", false);
    const deniedTableItem = createInvTableData(
      deniedItems,
      "denied",
      isDeniedRowDisabled
    );
    return [adjTableItem, deniedTableItem];
  }, [adjItems, deniedItems, referenceId, userConfiguration]);

  const tableConfig = {
    selectedItems: [] as any,
    apiStatus: "idle" as any,
    headerRules,
    data: getInvTableData(),
    page: 1,
    rowsPerPage: 25
  };

  return (
    <>
      <Box ref={ref}></Box>
      <Container sx={{ marginTop: "36px" }} maxWidth={"xl"}>
        <Container
          disableGutters
          sx={{
            backgroundColor: "grey.200"
          }}
          maxWidth={"xl"}
        >
          <Stack
            sx={{ padding: "16px 15px", height: "72px" }}
            justifyContent="center"
          >
            <Typography
              sx={{
                textTransform: "capitalize"
              }}
              color="grey.900"
            >
              {t("createInventoryAdjustment.adjustmentTitle")}
            </Typography>
          </Stack>
          <GoTableWithSelect tableConfig={tableConfig} />
        </Container>
      </Container>
    </>
  );
});

Adjustment.displayName = "Adjustment";

export default Adjustment;
